import { chatMessage, emitChatGetBacklog } from "@socket";
import { createSocketSubscription } from "@socket/utils";
import { socketInitiated } from "@store/customActions";
import { selectMeetingInstanceId } from "@store/selectors/meetingSelectors";
import {
  messageReceived,
  backlogLoaded,
  connectionStatusChanged,
} from "@store/chatSlice";
import {
  addNotificationToChatBox,
  removeNotificationFromChatBox,
} from "@store/notificationsSlice";

const BANNER_VISIBLE_MS = 5000;
const BANNER_THROTTLE_MS = 5500;
const BANNER_PREVIEW_MAX_CHARS = 40;

let currentProfileId = null;
let hasDisconnected = false;
let isBacklogFetchInFlight = false;
let lastBannerShownAt = 0;

const truncatePreview = (text) =>
  text.length > BANNER_PREVIEW_MAX_CHARS
    ? `${text.slice(0, BANNER_PREVIEW_MAX_CHARS)}…`
    : text;

const showMessageBanner = (api, message) => {
  const isOwnMessage = message.senderId === currentProfileId;
  const isChatPanelActive = api.getState().chat.panelActive;
  const isThrottled = Date.now() - lastBannerShownAt < BANNER_THROTTLE_MS;
  if (isOwnMessage || isChatPanelActive || isThrottled) return;

  lastBannerShownAt = Date.now();
  const bannerNonce = `${message.id}-${lastBannerShownAt}`;
  api.dispatch(
    addNotificationToChatBox({
      notification: {
        type: "message",
        nonce: bannerNonce,
        meta: {
          firstName: message.senderName,
          color: message.senderColor,
          avatar: message.senderAvatar,
          value: truncatePreview(message.text),
        },
      },
    })
  );

  setTimeout(() => {
    const bannerStillOwnsSlot =
      api.getState().notifications.chat?.nonce === bannerNonce;
    if (bannerStillOwnsSlot) {
      api.dispatch(removeNotificationFromChatBox());
    }
  }, BANNER_VISIBLE_MS);
};

const fetchBacklog = async (api, meetingInstanceId) => {
  if (isBacklogFetchInFlight || !meetingInstanceId) return;

  isBacklogFetchInFlight = true;
  try {
    const acknowledgement = await emitChatGetBacklog({ meetingInstanceId });
    if (acknowledgement?.ok) {
      api.dispatch(
        backlogLoaded({
          messages: acknowledgement.messages,
          profileId: currentProfileId,
        })
      );
    }
  } catch (error) {
    // Leave backlogLoaded false so the next connect event retries the fetch.
  } finally {
    isBacklogFetchInFlight = false;
  }
};

const chatSocketMiddlewares = (api) => (next) => (action) => {
  if (action.type === socketInitiated.type) {
    currentProfileId = action.payload.profileId;
    hasDisconnected = false;

    chatMessage.subscribe((message) => {
      api.dispatch(messageReceived({ message, profileId: currentProfileId }));
      showMessageBanner(api, message);
    });

    const connectSubscription = createSocketSubscription("connect");
    const disconnectSubscription = createSocketSubscription("disconnect");

    // disconnect needs allowMultiple: the meeting middleware already owns a
    // listener on it and subscribe() would otherwise silently no-op.
    disconnectSubscription.subscribe(() => {
      hasDisconnected = true;
      api.dispatch(connectionStatusChanged("disconnected"));
    }, true);

    connectSubscription.subscribe(() => {
      api.dispatch(connectionStatusChanged("connected"));

      const shouldRefetchBacklog =
        hasDisconnected || !api.getState().chat.backlogLoaded;
      if (shouldRefetchBacklog) {
        hasDisconnected = false;
        fetchBacklog(api, selectMeetingInstanceId(api.getState()));
      }
    });

    fetchBacklog(api, selectMeetingInstanceId(api.getState()));
  }

  return next(action);
};

export default chatSocketMiddlewares;
